/**
 * Generated by orval 🍺
 * Do not edit manually.
 * api API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import type { DownloadFileParams } from '../models/downloadFileParams';
import type { GetUploadBucket200 } from '../models/getUploadBucket200';
import type { GetUploadBucketParams } from '../models/getUploadBucketParams';
import type { UploadFileBody } from '../models/uploadFileBody';
import { executeFetch } from '../../utils/fetch/fetch';
import type { ErrorType } from '../../utils/fetch/fetch';

type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

/**
 * @summary Download File
 */
export const downloadFile = (
  fileId: string,
  params?: DownloadFileParams,
  options?: SecondParameter<typeof executeFetch>,
  signal?: AbortSignal,
) => {
  return executeFetch<void>(
    {
      url: `/api/file/download/${encodeURIComponent(String(fileId))}`,
      method: 'GET',
      params,
      signal,
    },
    options,
  );
};

export const getDownloadFileQueryKey = (fileId: string, params?: DownloadFileParams) => {
  return [`/api/file/download/${fileId}`, ...(params ? [params] : [])] as const;
};

export const getDownloadFileQueryOptions = <
  TData = Awaited<ReturnType<typeof downloadFile>>,
  TError = ErrorType<unknown>,
>(
  fileId: string,
  params?: DownloadFileParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadFile>>, TError, TData>>;
    request?: SecondParameter<typeof executeFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDownloadFileQueryKey(fileId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadFile>>> = ({ signal, meta }) =>
    downloadFile(fileId, params, { ...requestOptions, meta }, signal);

  return { queryKey, queryFn, enabled: !!fileId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof downloadFile>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type DownloadFileQueryResult = NonNullable<Awaited<ReturnType<typeof downloadFile>>>;
export type DownloadFileQueryError = ErrorType<unknown>;

/**
 * @summary Download File
 */
export const useDownloadFile = <
  TData = Awaited<ReturnType<typeof downloadFile>>,
  TError = ErrorType<unknown>,
>(
  fileId: string,
  params?: DownloadFileParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadFile>>, TError, TData>>;
    request?: SecondParameter<typeof executeFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getDownloadFileQueryOptions(fileId, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Download File
 */
export const prefetchDownloadFile = async <
  TData = Awaited<ReturnType<typeof downloadFile>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  fileId: string,
  params?: DownloadFileParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadFile>>, TError, TData>>;
    request?: SecondParameter<typeof executeFetch>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDownloadFileQueryOptions(fileId, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Upload Bucket
 */
export const getUploadBucket = (
  params?: GetUploadBucketParams,
  options?: SecondParameter<typeof executeFetch>,
  signal?: AbortSignal,
) => {
  return executeFetch<GetUploadBucket200>(
    { url: `/api/file/upload-bucket`, method: 'GET', params, signal },
    options,
  );
};

export const getGetUploadBucketQueryKey = (params?: GetUploadBucketParams) => {
  return [`/api/file/upload-bucket`, ...(params ? [params] : [])] as const;
};

export const getGetUploadBucketQueryOptions = <
  TData = Awaited<ReturnType<typeof getUploadBucket>>,
  TError = ErrorType<unknown>,
>(
  params?: GetUploadBucketParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUploadBucket>>, TError, TData>>;
    request?: SecondParameter<typeof executeFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUploadBucketQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUploadBucket>>> = ({ signal, meta }) =>
    getUploadBucket(params, { ...requestOptions, meta }, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getUploadBucket>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetUploadBucketQueryResult = NonNullable<Awaited<ReturnType<typeof getUploadBucket>>>;
export type GetUploadBucketQueryError = ErrorType<unknown>;

/**
 * @summary Get Upload Bucket
 */
export const useGetUploadBucket = <
  TData = Awaited<ReturnType<typeof getUploadBucket>>,
  TError = ErrorType<unknown>,
>(
  params?: GetUploadBucketParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUploadBucket>>, TError, TData>>;
    request?: SecondParameter<typeof executeFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetUploadBucketQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Get Upload Bucket
 */
export const prefetchGetUploadBucket = async <
  TData = Awaited<ReturnType<typeof getUploadBucket>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  params?: GetUploadBucketParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUploadBucket>>, TError, TData>>;
    request?: SecondParameter<typeof executeFetch>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetUploadBucketQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Upload File
 */
export const uploadFile = (
  uploadFileBody: UploadFileBody,
  options?: SecondParameter<typeof executeFetch>,
) => {
  const formData = new FormData();
  if (uploadFileBody.file !== undefined) {
    formData.append('file', uploadFileBody.file);
  }

  return executeFetch<void>(
    {
      url: `/api/file/upload-file`,
      method: 'POST',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
    },
    options,
  );
};

export const getUploadFileMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof uploadFile>>,
    TError,
    { data: UploadFileBody },
    TContext
  >;
  request?: SecondParameter<typeof executeFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof uploadFile>>,
  TError,
  { data: UploadFileBody },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof uploadFile>>,
    { data: UploadFileBody }
  > = (props) => {
    const { data } = props ?? {};

    return uploadFile(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UploadFileMutationResult = NonNullable<Awaited<ReturnType<typeof uploadFile>>>;
export type UploadFileMutationBody = UploadFileBody;
export type UploadFileMutationError = ErrorType<unknown>;

/**
 * @summary Upload File
 */
export const useUploadFile = <TError = ErrorType<unknown>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof uploadFile>>,
    TError,
    { data: UploadFileBody },
    TContext
  >;
  request?: SecondParameter<typeof executeFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof uploadFile>>,
  TError,
  { data: UploadFileBody },
  TContext
> => {
  const mutationOptions = getUploadFileMutationOptions(options);

  return useMutation(mutationOptions);
};
